<template>
  <div v-if="appSettingStore.loading === true"
    style="height:calc(90vh);display: flex;justify-content: center;align-items: center;">
    <t-loading size="large" indicator loading preventScrollThrough showOverlay :text="t('loading.dataLoading')"
      :inherit-color="true" class="gray-loading" />
  </div>
  <div v-else-if="isInAnyApp() && !isInApp() && !signInStore.mobileCompletionToken"
    style="height:calc(90vh);display: flex;justify-content: center;align-items: center;">
    <t-loading size="large" indicator loading preventScrollThrough showOverlay :text="t('loading.loginLoading')"
      :inherit-color="true" class="gray-loading" />
  </div>
  <div v-else class="login-wrapper" :class="themeClass"
    :style="{ backgroundImage: appSettingStore.currentBgImg ? `url(${appSettingStore.currentBgImg})` : '' }">
    <LanguageSwitcher />
    <div class="login-container">
      <div class="title-container">
        <!-- <h1 class="title margin-no">登录到</h1> -->
        <img class="logo" :src="appSettingStore.logoImg" alt="Logo" />
        <h2 class="title">{{ appSettingStore.name }}</h2>
      </div>
      <div class="item-container">
        <RouterView></RouterView>
      </div>
      <!-- <SignInModeSelector v-if="appSettingStore.showSignInModeSelector && !isInAnyApp() && !signInStore.mobileCompletionToken" /> -->
      <SignInModeSelector v-if="appSettingStore.showSignInModeSelector && !signInStore.mobileCompletionToken" />
      <template v-if="isLGWX && !signInStore.mobileCompletionToken">
        <t-divider>使用学校自有认证登录</t-divider>
        <div class="wx-icons-container">
          <t-image fit="contain" src="./hy.png" class="wx-icon" @click="navToThirdIDP('shou')" />
          <t-image fit="contain" src="./hs.png" class="wx-icon" @click="navToThirdIDP('shmtu')" />
          <t-image fit="contain" src="./dl.png" class="wx-icon" @click="navToThirdIDP('shiep')" />
          <t-image fit="contain" src="./dj.png" class="wx-icon" @click="navToThirdIDP('sdjq')" />
          <t-image fit="contain" src="./jq.png" class="wx-icon" @click="navToThirdIDP('gench')" />
        </div>
      </template>
    </div>
    <footer class="copyright">{{ appSettingStore.copyRight }}</footer>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAppSettingStore, useSignInStore } from './store/index_SignIn';
import { getCurrentLocale, setAppLocale } from './i18n';
import { isInWXWork, isInWeixin, isInAlipay, isInDingTalk, isInApp, isInAnyApp, supportsNativeWeChatAuth } from './utils/appDetection';

import SignInModeSelector from './components/SignInModeSelector.vue';
import LanguageSwitcher from './components/LanguageSwitcher.vue';

const appSettingStore = useAppSettingStore();
const signInStore = useSignInStore();
const router = useRouter();
const { t } = useI18n();

let bgInterval: number | null = null;

// 计算主题类名（用于组件内部）
const themeClass = computed(() => {
  const cssType = appSettingStore.indexCss || 'default';
  return `theme-${cssType}`;
});

// 启动背景图片轮播
const startBgRotation = () => {
  if (appSettingStore.bgImgs && appSettingStore.bgImgs.length > 1) {
    bgInterval = setInterval(() => {
      appSettingStore.nextBgImg();
    }, 5000);
  }
};

// 停止背景图片轮播
const stopBgRotation = () => {
  if (bgInterval) {
    clearInterval(bgInterval);
    bgInterval = null;
  }
};

const getQueryStringByName = (name: string) => {
  var result = location.search.match(new RegExp("[\?\&]" + name + "=([^\&]+)", "i"));
  if (result == null || result.length < 1) {
    return '';
  }

  return result[1];
};

onMounted(async () => {
  if (isInApp()) {
    // 等待 Bridge 就绪（推荐），确保在调用任何 Bridge 方法之前 Bridge 已经准备好
    window.addEventListener('appBridgeReady', async () => {
      const info = await window.getAppInfo();
      console.log(info);
    });
  }

  setAppLocale(appSettingStore.currentLocale);

  var returnUrl = getQueryStringByName('ReturnUrl');
  signInStore.returnUrl = returnUrl;
  signInStore.mobileCompletionToken = decodeURIComponent(getQueryStringByName('MobileCompletionToken'));

  await appSettingStore.getSignInConfig();

  if (signInStore.mobileCompletionToken) {
    startBgRotation();
    router.replace('/CompleteMobile');
    return;
  }

  if (isInWeixin() && appSettingStore.enableWXQRLogin) {
    location.href = `./external/Challenge?provider=WeChat&returnUrl=${signInStore.returnUrl}`;
  } else if (isInWXWork() && appSettingStore.enableQYWXQRLogin) {
    location.href = `./external/Challenge?provider=WeChat&corp=true&returnUrl=${signInStore.returnUrl}`;
  } else if (isInAlipay() && appSettingStore.enableAlipayLogin) {
    location.href = `./external/Challenge?provider=Alipay&returnUrl=${signInStore.returnUrl}`;
  } else if (isInDingTalk() && appSettingStore.enableDingTalkLogin) {
    location.href = `./external/Challenge?provider=DingTalk.建桥小星&returnUrl=${signInStore.returnUrl}`;
  } else {
    // 启动背景轮播
    startBgRotation();

    if (appSettingStore.enableAccountLogin === true && appSettingStore.signInCaptchaMode === '3') {
      appSettingStore.loadTencentCaptchaScript();
    }

    if (appSettingStore.enableAccountLogin === true) {
      router.replace('/AccountPassword');
    }
    else if (appSettingStore.enableWXQRLogin === true || (supportsNativeWeChatAuth() && appSettingStore.enableWXNativeAppLogin === true)) {
      router.replace('/WX');
    }
    else if (appSettingStore.enableQYWXQRLogin === true) {
      router.replace('/WXWork');
    }
    else if (appSettingStore.enableDingTalkLogin === true) {
      router.replace('/DingTalk');
    }
    else if (appSettingStore.enableAlipayLogin === true) {
      router.replace('/Alipay');
    }
    else {
      router.replace('/Result/error/错误/系统配置错误/false');
    }
  }
});

const isLGWX = computed(() => {
  console.log('Current theme class:', themeClass.value);
  return themeClass.value === 'theme-lgwx';
});

const navToThirdIDP = (scheme: string) => {
  var challengeUrl = `./external/Challenge?provider=${scheme}&returnUrl=${signInStore.returnUrl ? signInStore.returnUrl : '/FAP5.Portal'}`;
  location.href = challengeUrl;
};

onBeforeUnmount(() => {
  stopBgRotation();
});
</script>

<style lang="less">
@import url('./style/index.less');
@import url('./style/index_default.less');
@import url('./style/index_uat.less');
@import url('./style/index_gench.less');
@import url('./style/index_shpc.less');
@import url('./style/index_shiep.less');
@import url('./style/index_pzy.less');
@import url('./style/index_lgwx.less');

.gray-loading {
  color: #999; // 让指示器和文字都变灰（依赖 inherit-color）
}

.wx-icons-container {
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  margin-top: 20px;
}

.wx-icon {
  width: 40px;
  height: 40px;
  background-color: transparent;
  cursor: pointer;
}
</style>
